// src/pages/RoleManagement.jsx
//  List roles and assign permissions (requires roles.read / roles.write).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Checkbox,
  Button,
  message,
  Typography,
  Space,
  Spin,
  Tag,
  Row,
  Col,
} from "antd";
import {
  SafetyCertificateOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getRolesApi, updateRolePermissionsApi } from "../api/role.api";
import { useAuth } from "../context/AuthContext";

const { Title, Text } = Typography;

const RoleManagement = () => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("roles.write");

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selection, setSelection] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getRolesApi();
      setRoles(data.data.roles);
      setAllPermissions(data.data.all_permissions || []);
      const next = {};
      data.data.roles.forEach((r) => {
        next[r.id] = r.permission_ids || [];
      });
      setSelection(next);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to load roles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byResource = useMemo(() => {
    const m = {};
    allPermissions.forEach((p) => {
      if (!m[p.resource]) m[p.resource] = [];
      m[p.resource].push(p);
    });
    return m;
  }, [allPermissions]);

  const handleSave = async (roleId) => {
    try {
      await updateRolePermissionsApi(roleId, selection[roleId] || []);
      message.success("Permissions updated.");
      load();
    } catch (err) {
      message.error(err.response?.data?.message || "Save failed.");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row
        align="middle"
        justify="space-between"
        style={{ marginBottom: 20, padding: "10px 10px" }}
      >
        <Col>
          <Space>
            <SafetyCertificateOutlined
              style={{ fontSize: 22, color: "black" }}
            />
            <Title level={4} style={{ margin: 0, color: "black" }}>
              Roles and permissions
            </Title>
            <Tag color="blue">{roles.length} roles</Tag>
          </Space>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Toggle permissions per role. Users inherit permissions from their role.
      </Text>

      {roles.map((role) => (
        <Card
          key={role.id}
          title={
            <Space>
              <Text strong style={{ textTransform: "capitalize" }}>
                {role.name}
              </Text>
              <Tag>id {role.id}</Tag>
            </Space>
          }
          style={{ marginBottom: 16, borderRadius: 12 }}
        >
          {Object.entries(byResource).map(([resource, perms]) => (
            <div key={resource} style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                {resource}
              </Text>
              <Checkbox.Group
                options={perms.map((p) => ({
                  label: `${p.label} (${p.code})`,
                  value: p.id,
                }))}
                value={selection[role.id] || []}
                onChange={(vals) =>
                  setSelection((prev) => ({ ...prev, [role.id]: vals }))
                }
                disabled={!canWrite}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              />
            </div>
          ))}
          {canWrite && (
            <Button
              type="primary"
              onClick={() => handleSave(role.id)}
              style={{ background: "rgb(120, 57, 120)", border: "none" }}
            >
              Save {role.name}
            </Button>
          )}
        </Card>
      ))}
    </div>
  );
};

export default RoleManagement;
